import type { NodeViewRenderer, NodeViewRendererProps } from '@tiptap/core';
import { findParentNodeClosestToPos } from '@tiptap/core';
import type { Node, Node as PmNode, ResolvedPos } from '@tiptap/pm/model';
import { NodeSelection } from '@tiptap/pm/state';
import classNames from 'classnames';
import type { Node as ProseMirrorNode } from 'prosemirror-model';

import { TIPTAP_LAYOUT_CELL_TYPE, TIPTAP_LAYOUT_TYPE } from './../../ricos-content';
import { Node_Type } from './../../ricos-schema/next-schema';
import { logInvariant } from './../../_shared/log-invariant';
import { findNodeById } from './../../tiptap-editor/helpers';

import { LAYOUT_HANDLE } from './data-hooks';
import styles from './layouts-scss';
import {
  balanceLayoutCells,
  findLayoutOfCell,
  getHandleSmallIcon,
  MAX_CELLS,
  MAX_COLUMNS,
  MIN_COL_SPAN,
} from './shared';

type LayoutNodeViewState = {
  columnTemplate: number[];
  isResizing: boolean;
  isDragging: boolean;
  invalid: 'none' | 'left' | 'right';
  dropCursorPosition: null | number;
  isDraggingOver: boolean;
};

export const layoutNodeView =
  (disableInteractions: boolean): NodeViewRenderer =>
  ({ node, getPos, editor }) => {
    const state: LayoutNodeViewState = {
      columnTemplate: getGridTemplateColumns(node),
      isResizing: false,
      isDragging: false,
      invalid: 'none',
      dropCursorPosition: null,
      isDraggingOver: false,
    };
    const layoutWrapper = document.createElement('div');
    layoutWrapper.classList.add(styles.layoutWrapper);

    const layoutElement = document.createElement('div');

    layoutWrapper.append(layoutElement);

    // Layout handle must be wrapped in a div with contenteditable=false to prevent setting selection inside layoutElement
    // on pointerdown
    const layoutDragHandleWrapper = document.createElement('div');
    layoutDragHandleWrapper.setAttribute('contenteditable', 'false');
    layoutDragHandleWrapper.classList.add(styles.layoutHandlerWrapper);

    const layoutDragHandle = document.createElement('button');
    layoutDragHandle.setAttribute('data-drag-handle', '');
    layoutDragHandle.setAttribute('data-layout-handle', '');
    layoutDragHandle.setAttribute('type', 'button');
    layoutDragHandle.classList.add(styles.layoutHandler);
    layoutDragHandle.innerHTML = getHandleSmallIcon(styles.layoutHandlerIcon);

    layoutDragHandleWrapper.append(layoutDragHandle);
    layoutWrapper.append(layoutDragHandleWrapper);

    const dropCursorElement = document.createElement('div');
    dropCursorElement.classList.add(styles.dropCursor);

    const setState = (newState: Partial<LayoutNodeViewState>) => {
      // We need to keep ref to state object as updates can be triggered by ProseMirror and by us manually. We also we
      // need to keep latest state values in event listeners avoiding closure issues
      Object.assign(state, newState);

      updateLayoutView({
        layoutWrapper,
        layoutElement,
        layoutDragHandle,
        dropCursorElement,
        state,
        node,
        getPos,
        editor,
      });
    };

    updateLayoutView({
      layoutWrapper,
      layoutElement,
      layoutDragHandle,
      dropCursorElement,
      state,
      node,
      getPos,
      editor,
    });

    const cleanups = disableInteractions
      ? [
          attachLayoutClickHandler({
            layoutDragHandle,
            layoutElement,
            editor,
          }),
        ]
      : [
          attachCellResizeHandler({ layoutElement, state, editor, setState }),
          attachCellDragHandler({ layoutElement, editor, setState }),
          attachLayoutDragHandler({
            layoutWrapper,
            layoutElement,
            editor,
            setState,
          }),
          attachLayoutClickHandler({
            layoutDragHandle,
            layoutElement,
            editor,
          }),
        ];

    return {
      dom: layoutWrapper,
      contentDOM: layoutElement,
      update: (newNode: PmNode) => {
        if (newNode.type !== node.type) {
          return false;
        }
        if (!state.isResizing) {
          state.columnTemplate = getGridTemplateColumns(newNode);
        }
        // eslint-disable-next-line no-param-reassign
        node = newNode;
        updateLayoutView({
          layoutWrapper,
          layoutElement,
          layoutDragHandle,
          dropCursorElement,
          state,
          node: newNode,
          getPos,
          editor,
        });
        return true;
      },
      ignoreMutation: (mutation: MutationRecord) => {
        const isMutatingDataAttribute =
          mutation.type === 'attributes' && mutation.attributeName?.startsWith('data-');
        const isMutatingLayoutAttribute =
          mutation.target === layoutElement && mutation.type === 'attributes';
        const isTogglingLayoutDropCursor =
          mutation.target === layoutWrapper &&
          mutation.type === 'childList' &&
          (mutation.addedNodes[0] === dropCursorElement ||
            mutation.removedNodes[0] === dropCursorElement);
        const isMutatingDropCursor = mutation.target === dropCursorElement;

        return (
          isMutatingDataAttribute ||
          isMutatingLayoutAttribute ||
          isTogglingLayoutDropCursor ||
          isMutatingDropCursor
        );
      },
      destroy: () => {
        cleanups.forEach(cleanup => cleanup());
      },
    };
  };

const updateLayoutView = ({
  layoutWrapper,
  layoutElement,
  layoutDragHandle,
  dropCursorElement,
  state,
  node,
  getPos,
  editor,
}: Pick<NodeViewRendererProps, 'node' | 'getPos' | 'editor'> & {
  layoutWrapper: HTMLDivElement;
  layoutElement: HTMLDivElement;
  layoutDragHandle: HTMLButtonElement;
  dropCursorElement: HTMLDivElement;
  state: LayoutNodeViewState;
}) => {
  const { $to, $from } = editor.state.selection;
  const pos = getPos();
  const isSelected = pos === $from.pos && pos + node.nodeSize === $to.pos;
  const inLayout = isInLayout($to, node.attrs.id);

  layoutDragHandle.setAttribute('data-hook', `${LAYOUT_HANDLE}-${node.attrs.id}`);

  layoutElement.setAttribute('data-layout-container', node.attrs.id);
  layoutElement.style.setProperty(
    '--ricos-layout-column-template',
    formatGridTemplateColumns(state.columnTemplate)
  );

  layoutElement.classList.value = classNames({
    [styles.layout]: true,
    [styles.draggingOver]: state.isDraggingOver || state.dropCursorPosition !== null,
    [styles.invalidLeft]: state.invalid === 'left',
    [styles.invalidRight]: state.invalid === 'right',
    [styles.resizing]: state.isResizing,
    [styles.dragging]: state.isDragging,
    [styles.layoutSelected]: isSelected,
    [styles.inLayout]: inLayout,
  });

  if (state.dropCursorPosition === null) {
    dropCursorElement.remove();
  } else {
    dropCursorElement.style.setProperty(
      '--ricos-layout-drop-cursor-position',
      `${state.dropCursorPosition}px`
    );
    if (!layoutWrapper.contains(dropCursorElement)) {
      layoutWrapper.append(dropCursorElement);
    }
  }
};

const getGridTemplateColumns = (node: Node) => {
  const cols: number[] = [];

  node.content.forEach(cell => {
    if ((cell.type.name as unknown as Node_Type) === Node_Type.LAYOUT_CELL) {
      cols.push(cell.attrs.colSpan);
    }
  });

  return cols;
};

const formatGridTemplateColumns = (colSpans: number[]) => {
  return colSpans.map(col => `minmax(0, ${col}fr)`).join(' ');
};

const isInLayout = ($pos: ResolvedPos, nodeId: string) => {
  const layoutNode = findParentNodeClosestToPos(
    $pos,
    node => node.type?.name === TIPTAP_LAYOUT_TYPE
  )?.node;

  return layoutNode?.attrs.id === nodeId;
};

const getCellParent = (node: ProseMirrorNode, cellId: string): ProseMirrorNode | null => {
  let parent: ProseMirrorNode | null = null;
  node.descendants((child, _pos, par) => {
    if (child.attrs.id === cellId) {
      parent = par;
    }
  });

  return parent;
};

const getCellIndex = (node: Node, cellId: string) => {
  let index = -1;

  node.content.forEach((cell, _offset, i) => {
    if (cell.attrs.id === cellId) {
      index = i;
    }
  });

  return index;
};

const isDraggingCell = (editor: NodeViewRendererProps['editor']) => {
  const slice = editor.view.dragging?.slice;

  if (!slice) {
    return false;
  }

  return (
    slice.openStart === 0 &&
    slice.openEnd === 0 &&
    slice.content.childCount === 1 &&
    editor.view.dragging?.slice.content.firstChild?.type.name === TIPTAP_LAYOUT_CELL_TYPE
  );
};

const attachCellResizeHandler = ({
  layoutElement,
  state,
  editor,
  setState,
}: Pick<NodeViewRendererProps, 'editor'> & {
  layoutElement: HTMLElement;
  state: LayoutNodeViewState;
  setState: (state: Partial<LayoutNodeViewState>) => void;
}) => {
  const onPointerDown = (event: PointerEvent) => {
    const dragHandle: HTMLElement | null = (event.target as HTMLElement)?.closest(
      '[data-layout-cell-resize-handle]'
    );

    if (!dragHandle || !event.currentTarget) {
      return;
    }

    const cellNodeId = dragHandle.dataset.layoutCellResizeHandle;

    if (!cellNodeId) {
      logInvariant(
        Error(
          'Cell resize prevented. No cell id found! Make sure to add the data-layout-cell-resize-handle attribute to the drag handle'
        )
      );
      return;
    }
    event.preventDefault();

    const layoutNode = getCellParent(editor.state.doc, cellNodeId);

    if (!layoutNode) {
      logInvariant(Error('Cell resize prevented. Draggable cell has no parent!'));
      return;
    }

    const index = getCellIndex(layoutNode, cellNodeId);
    const currentOffset = event.clientX;
    const parentRect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const fractionInPixels = parentRect.width / MAX_COLUMNS;
    const minColumnWidth = fractionInPixels * MIN_COL_SPAN;
    const columnTemplateInFractionsByPixel = state.columnTemplate.map(
      col => col * fractionInPixels
    );

    setState({
      columnTemplate: columnTemplateInFractionsByPixel,
      isResizing: true,
    });

    const onDrag = (event: PointerEvent) => {
      event.preventDefault();
      let invalid: 'none' | 'left' | 'right' = 'none';

      const offset = currentOffset - event.clientX;

      const newTemplate = [...columnTemplateInFractionsByPixel];
      newTemplate[index] = columnTemplateInFractionsByPixel[index] + offset;
      newTemplate[index - 1] = columnTemplateInFractionsByPixel[index - 1] - offset;

      if (newTemplate[index] < minColumnWidth) {
        const diff = minColumnWidth - newTemplate[index];
        newTemplate[index] = minColumnWidth;
        newTemplate[index - 1] -= diff;
        invalid = 'right';
      }

      if (newTemplate[index - 1] < minColumnWidth) {
        const diff = minColumnWidth - newTemplate[index - 1];
        newTemplate[index - 1] = minColumnWidth;
        newTemplate[index] -= diff;
        invalid = 'left';
      }

      if (newTemplate.some(col => col < minColumnWidth)) {
        setState({ invalid });
        return;
      }

      setState({
        columnTemplate: newTemplate,
        invalid,
      });
    };

    const stopDrag = (event: PointerEvent) => {
      event.preventDefault();

      const templateInFractions = state.columnTemplate.map(col => {
        return Math.round(col / fractionInPixels);
      });

      const diff = MAX_COLUMNS - templateInFractions.reduce((acc, val) => acc + val, 0);
      templateInFractions[index] += diff;

      editor.commands.setCellsColSpan(layoutNode.attrs.id, templateInFractions);

      setState({
        invalid: 'none',
        isResizing: false,
        columnTemplate: templateInFractions,
      });

      window.removeEventListener('pointerup', stopDrag);
      window.removeEventListener('pointermove', onDrag);
    };

    window.addEventListener('pointerup', stopDrag);
    window.addEventListener('pointermove', onDrag);
  };

  layoutElement.addEventListener('pointerdown', onPointerDown);

  return () => {
    layoutElement.removeEventListener('pointerdown', onPointerDown);
  };
};

const attachCellDragHandler = ({
  layoutElement,
  editor,
  setState,
}: Pick<NodeViewRendererProps, 'editor'> & {
  layoutElement: HTMLElement;
  setState: (state: Partial<LayoutNodeViewState>) => void;
}) => {
  const onDragStart = (event: DragEvent) => {
    const target = event.target as HTMLElement;
    const cellNodeId = target?.getAttribute('data-layout-cell');
    if (!cellNodeId || !target) {
      return;
    }

    editor.commands.selectCell(cellNodeId);
    event.dataTransfer?.setDragImage(target, 0, 0);
  };

  // This is heavily based on prosemirror-view `drop` event. We only need this because PM-View can't
  // handle position resolving for vertical content.
  // https://github.com/ProseMirror/prosemirror-view/blob/0954f523ee0a193f7db3992702e6a3979ade89b8/src/input.ts#L702
  const onDrop = (event: DragEvent) => {
    if (!isDraggingCell(editor) || !event.target) {
      return;
    }
    event.stopPropagation();
    setState({ dropCursorPosition: null });
    const view = editor.view;
    const dragging = view.dragging;
    const slice = dragging?.slice;
    view.dragging = null;

    if (!slice) {
      logInvariant(Error('Cell Drag prevented. PM view has no dragging slice'));
      return;
    }

    const sourceCell = slice.content.firstChild;
    const sourceCellId = sourceCell?.attrs.id;
    const eventPos = view.posAtDOM(event.target as HTMLElement, 0);
    const $eventPos = view.state.doc.resolve(eventPos);
    if (!eventPos || !sourceCellId) {
      logInvariant(Error('Cell Drag prevented. Cannot resolve event position or source cell id'));
      return;
    }

    const layoutNode = findParentNodeClosestToPos(
      $eventPos,
      node => node.type?.name === TIPTAP_LAYOUT_TYPE
    )?.node;

    if (!layoutNode) {
      logInvariant(Error('Cell Drag prevented. Cannot resolve target layout node'));
      return;
    }

    const cellIndexInSourceLayout = getCellIndex(layoutNode, sourceCellId);
    const isCellMovedToOtherLayout = cellIndexInSourceLayout === -1;
    if (layoutNode.childCount >= MAX_CELLS && isCellMovedToOtherLayout) {
      return;
    }

    const { node: cellNode, pos: cellPos } = findParentNodeClosestToPos(
      $eventPos,
      node => node.type?.name === TIPTAP_LAYOUT_CELL_TYPE
    ) ?? { node: null, pos: -1 };

    if (!cellNode || (layoutNode.childCount === 1 && layoutNode.firstChild === sourceCell)) {
      return;
    }

    const layout = event.currentTarget as HTMLElement;
    const layoutRect = layout.getBoundingClientRect();
    const cell = (event.target as HTMLElement).closest('[data-layout-cell]');

    if (!cell) {
      logInvariant(Error('Cell Drag prevented. Cannot find cell element'));
      return;
    }

    const mouseXInLayout = event.clientX - layoutRect.left;
    const cellRect = cell.getBoundingClientRect();

    const insertPos =
      mouseXInLayout < cellRect.left - layoutRect.left + cellRect.width / 2
        ? cellPos
        : cellPos + cellNode?.nodeSize;

    if (!insertPos) {
      logInvariant(Error('Cell Drag prevented. Cannot resolve insert position'));
      return;
    }

    event.preventDefault();

    const tr = view.state.tr;
    // @ts-expect-error
    const { node } = dragging;

    if (node) {
      node.replace(tr);
    } else {
      tr.deleteSelection();
    }

    const pos = tr.mapping.map(insertPos);
    const beforeInsert = tr.doc;

    if (!slice.content.firstChild) {
      return;
    }

    tr.replaceRangeWith(pos, pos, slice.content.firstChild);

    if (tr.doc.eq(beforeInsert)) {
      return;
    }

    const $pos = tr.doc.resolve(pos);

    tr.setSelection(new NodeSelection($pos));

    if (sourceCellId) {
      const sourceLayoutNode = findLayoutOfCell(view.state.doc, sourceCellId);
      if (sourceLayoutNode) {
        // If cell was moved to another layout, balance the cells in the source layout
        const sourceLayoutInNewTr = findNodeById(tr, sourceLayoutNode.node.attrs.id).pop();
        if (sourceLayoutInNewTr && isCellMovedToOtherLayout) {
          balanceLayoutCells(tr, sourceLayoutInNewTr.node, sourceLayoutInNewTr.pos);
        }
      }

      // If cell was moved to another layout, balance the cells in the target layout
      const targetLayoutNode = findLayoutOfCell(tr.doc, sourceCellId);
      if (targetLayoutNode && isCellMovedToOtherLayout) {
        balanceLayoutCells(tr, targetLayoutNode.node, targetLayoutNode.pos);
      }
    }

    view.focus();
    view.dispatch(tr);
  };

  const onDragOver = (event: DragEvent) => {
    setState({
      isDraggingOver: true,
    });

    if (!isDraggingCell(editor)) {
      return;
    }

    const layout = event.currentTarget as HTMLElement;
    const layoutRect = layout.getBoundingClientRect();
    const cellDropAreas = [...layout.querySelectorAll('[data-layout-cell]')].map(cell => {
      const rect = cell.getBoundingClientRect();
      return {
        left: rect.left - layoutRect.left,
        right: rect.right - layoutRect.left,
        width: rect.width,
      };
    });
    const mouseXInLayout = event.clientX - layoutRect.left;
    const cellInsideIndex = cellDropAreas.findIndex(cell => {
      return mouseXInLayout > cell.left && mouseXInLayout < cell.right;
    });

    if (cellInsideIndex === -1) {
      return;
    }

    const cellInside = cellDropAreas[cellInsideIndex];
    const dropIndex =
      mouseXInLayout < cellInside.left + cellInside.width / 2
        ? cellInsideIndex
        : cellInsideIndex + 1;

    setState({
      dropCursorPosition:
        dropIndex >= cellDropAreas.length
          ? cellDropAreas[cellDropAreas.length - 1].right
          : cellDropAreas[dropIndex].left,
    });
  };

  const onDragLeave = (event: DragEvent) => {
    if ((event.currentTarget as HTMLElement).contains(event.relatedTarget as HTMLElement)) {
      return;
    }

    setState({
      dropCursorPosition: null,
      isDraggingOver: false,
    });
  };

  layoutElement.addEventListener('dragstart', onDragStart);
  layoutElement.addEventListener('drop', onDrop);
  layoutElement.addEventListener('dragover', onDragOver);
  layoutElement.addEventListener('dragleave', onDragLeave);

  return () => {
    layoutElement.removeEventListener('dragstart', onDragStart);
    layoutElement.removeEventListener('drop', onDrop);
    layoutElement.removeEventListener('dragover', onDragOver);
    layoutElement.removeEventListener('dragleave', onDragLeave);
  };
};

const attachLayoutDragHandler = ({
  layoutWrapper,
  layoutElement,
  editor,
  setState,
}: Pick<NodeViewRendererProps, 'editor'> & {
  layoutWrapper: HTMLElement;
  layoutElement: HTMLElement;
  setState: (state: Partial<LayoutNodeViewState>) => void;
}) => {
  const onDragStart = (event: DragEvent) => {
    const cell = (event.target as HTMLElement).closest('[data-layout-cell]');
    const layoutId = layoutElement.getAttribute('data-layout-container');

    if (cell) {
      return;
    }

    if (layoutId) {
      editor.commands.selectLayout(layoutId);
    }
    event.dataTransfer?.setDragImage(layoutElement, 0, 0);
    setState({ isDragging: true });

    const removeDragging = () => {
      setState({ isDragging: false });
      window.removeEventListener('pointerup', removeDragging);
    };

    window.addEventListener('pointerup', removeDragging);
  };

  layoutWrapper.addEventListener('dragstart', onDragStart);

  return () => {
    layoutWrapper.removeEventListener('dragstart', onDragStart);
  };
};

const attachLayoutClickHandler = ({
  layoutDragHandle,
  layoutElement,
  editor,
}: Pick<NodeViewRendererProps, 'editor'> & {
  layoutDragHandle: HTMLElement;
  layoutElement: HTMLElement;
}) => {
  const onClick = () => {
    const layoutId = layoutElement.getAttribute('data-layout-container');

    if (layoutId) {
      editor.chain().selectLayout(layoutId).focus().run();
    }
  };

  layoutDragHandle.addEventListener('click', onClick);

  return () => {
    layoutDragHandle.removeEventListener('click', onClick);
  };
};
